import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Staff } from '@mygate/common';

interface AuthState {
  isAuthenticated: boolean;
  staff: Pick<Staff, 'id' | 'firstName' | 'lastName' | 'role'> | null;
  phoneNumber: string | null; // For OTP flow
  accessToken: string | null;
  refreshToken: string | null;
}

interface AuthActions {
  setPhoneNumber: (phoneNumber: string) => void;
  setStaff: (staff: AuthState['staff']) => void;
  setAuthenticated: (authenticated: boolean) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  clearTokens: () => void;
  logout: () => void;
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      staff: null,
      phoneNumber: null,
      accessToken: null,
      refreshToken: null,

      setPhoneNumber: phoneNumber => set({ phoneNumber }),

      setStaff: staff =>
        set({
          staff,
          isAuthenticated: true,
        }),

      setAuthenticated: authenticated =>
        set({
          isAuthenticated: authenticated,
        }),

      setTokens: (accessToken, refreshToken) =>
        set({
          accessToken,
          refreshToken,
          isAuthenticated: true,
        }),

      clearTokens: () =>
        set({
          accessToken: null,
          refreshToken: null,
        }),

      logout: () =>
        set({
          isAuthenticated: false,
          staff: null,
          phoneNumber: null,
          accessToken: null,
          refreshToken: null,
        }),

      getAccessToken: () => get().accessToken,
      getRefreshToken: () => get().refreshToken,
    }),
    {
      name: 'backoffice-auth',
      partialize: state => ({
        // Persist tokens and staff info
        staff: state.staff,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
